import { useEffect, useRef, useState } from 'react';
import { impactCounters as defaultCounters } from '@/mocks/homeData';
import { useSiteData } from '@/contexts/SiteDataContext';

function CounterItem({ value, suffix, label, icon, index }: { value: number; suffix: string; label: string; icon: string; index: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = value / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= value) {
              setCount(value);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div
      ref={ref}
      className="flex flex-col items-center text-center p-4 rounded-2xl hover:bg-background-100 transition-all duration-300 animate-fade-in-up"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mb-3">
        <i className={`${icon} text-primary-500 text-xl`}></i>
      </div>
      <div className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
        {count.toLocaleString()}{suffix}
      </div>
      <p className="text-foreground-500 text-xs md:text-sm mt-1 font-medium">{label}</p>
    </div>
  );
}

export default function ImpactCounters() {
  const { data } = useSiteData();
  const impactCounters = data.impactCounters.length > 0 ? data.impactCounters : defaultCounters;
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 bg-primary-50 text-primary-600 text-xs font-semibold rounded-full mb-3">
            Our Impact
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-3">
            Numbers That <span className="gradient-text">Tell Stories</span>
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
            Every number represents a life touched, a dream supported, and a community transformed through your generosity.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {impactCounters.map((counter, index) => (
            <CounterItem key={index} {...counter} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}